/**
 * The times "Send later" offers with one click, and how a send time is written.
 *
 * Pure, and handed "now" rather than reading the clock, so the tests can stand
 * on a Friday afternoon or a Monday morning and check what the buttons say.
 * Everything is in the browser's own time zone - the operator scheduling for
 * "tomorrow at nine" means their nine, and the server is given the instant.
 */

/** The one-click choices. Keys are catalogue keys under `drafts.quick`. */
export type QuickPick = "inHour" | "tomorrowMorning" | "mondayMorning";

/** The hour a "morning" pick lands on. */
export const MORNING_HOUR = 9;

/** An hour from now, rounded up to the next quarter so the time reads as chosen. */
function inAnHour(now: Date): Date {
  const at = new Date(now.getTime() + 60 * 60_000);
  const quarter = 15;
  const minutes = at.getMinutes();
  const up = Math.ceil(minutes / quarter) * quarter;
  at.setMinutes(up, 0, 0);
  return at;
}

function morningOf(day: Date): Date {
  const at = new Date(day);
  at.setHours(MORNING_HOUR, 0, 0, 0);
  return at;
}

/**
 * The quick picks for this moment.
 *
 * "Monday morning" is left out when tomorrow already is Monday - two buttons
 * naming the same instant is one button too many - and on a Monday it means
 * next week's, because this Monday's morning has either passed or is the
 * "tomorrow" of nobody.
 */
export function quickPicks(now: Date): { key: QuickPick; at: Date }[] {
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const picks: { key: QuickPick; at: Date }[] = [
    { key: "inHour", at: inAnHour(now) },
    { key: "tomorrowMorning", at: morningOf(tomorrow) },
  ];
  // getDay(): 0 is Sunday, 1 Monday. Days until the next Monday, never 0.
  const toMonday = ((8 - now.getDay()) % 7) || 7;
  if (toMonday !== 1) {
    const monday = new Date(now);
    monday.setDate(now.getDate() + toMonday);
    picks.push({ key: "mondayMorning", at: morningOf(monday) });
  }
  return picks;
}

/** Whether a picked time is far enough ahead to schedule (the server asks a minute). */
export function isSchedulable(iso: string, now: Date): boolean {
  const at = new Date(iso);
  return !Number.isNaN(at.getTime()) && at.getTime() >= now.getTime() + 60_000;
}

/** "Fri 26.09 09:00" in the operator's language - the weekday first, because
 *  that is what someone scheduling for "Friday" checks. */
export function formatSendTime(iso: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const tag = locale.toLowerCase().startsWith("et") ? "et-EE" : "en-GB";
  const day = new Intl.DateTimeFormat(tag, { weekday: "short", day: "2-digit", month: "2-digit" }).format(at);
  const time = new Intl.DateTimeFormat(tag, { hour: "2-digit", minute: "2-digit" }).format(at);
  return `${day} ${time}`;
}
